import React, { useContext, useEffect, useState } from "react";
import ConsolesBoard from "../components/ConsolesBoard";
import AnimatedBackground from "./AnimatedBackground";
import { AuthContext } from "../context/AuthContext";
import { SelectionContext } from "../context/SelectionContext";
import { useNavigate } from "react-router-dom";
import AdminNavbar from "../components/AdminNavbar";
import io from "socket.io-client";

const socket = io(process.env.REACT_APP_API_URL);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const { selections, setSelections } = useContext(SelectionContext);

  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dailyRevenue, setDailyRevenue] = useState("0.00");
  const [showPricePopup, setShowPricePopup] = useState(false);
  const [priceToDisplay, setPriceToDisplay] = useState("");
  const [showJournal, setShowJournal] = useState(false);
  const [todaySessions, setTodaySessions] = useState([]);

  const [consoles, setConsoles] = useState([
    { id: "ps4-1", name: "PS4 - 1", plateforme: "PS4", statut: "libre", jeu: null },
    { id: "ps5-1", name: "PS5 - 1", plateforme: "PS5", statut: "libre", jeu: null },
    { id: "ps4-2", name: "PS4 - 2", plateforme: "PS4", statut: "libre", jeu: null },
    { id: "ps5-2", name: "PS5 - 2", plateforme: "PS5", statut: "libre", jeu: null },
  ]);

  // 🔁 Synchroniser avec localStorage au chargement
  useEffect(() => {
    const saved = localStorage.getItem("selections");
    if (saved) {
      try {
        setSelections(JSON.parse(saved));
      } catch (e) {
        console.error("Erreur parsing localStorage selections :", e);
      }
    }
  }, [setSelections]);

  // 🔄 Enregistrer automatiquement les changements
  useEffect(() => {
    localStorage.setItem("selections", JSON.stringify(selections));
  }, [selections]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const fetchDailyRevenue = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions/today-revenue`);
      const data = await res.json();
      setDailyRevenue(data.total || "0.00");
    } catch (err) {
      console.error("Erreur chargement recette du jour :", err);
    }
  };

  const fetchTodaySessions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions/today`);
      const data = await res.json();
      setTodaySessions(data);
    } catch (err) {
      console.error("Erreur chargement sessions du jour :", err);
    }
  };

  useEffect(() => {
    fetchDailyRevenue();
    fetchTodaySessions();

    socket.on("sessionCreated", () => {
      fetchDailyRevenue();
      fetchTodaySessions();
    });

    return () => {
      socket.off("sessionCreated");
    };
  }, []);

  const handleShowPrice = (price) => {
    setPriceToDisplay(price);
    setShowPricePopup(true);
    fetchDailyRevenue();
    fetchTodaySessions();
    setTimeout(() => setShowPricePopup(false), 1000);
  };

  const handleLogout = () => {
    if (window.confirm("Souhaitez-vous vous déconnecter ?")) {
      localStorage.removeItem("token");
      setUser(null);
      navigate("/login");
    }
  };

  const handleOpenJournal = () => {
    fetchTodaySessions();
    setShowJournal(!showJournal);
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm("Confirmez-vous la suppression de cette session ?")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sessions/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (res.ok) {
        alert("Session supprimée avec succès");
        fetchTodaySessions();
      } else {
        alert(data.error || "Erreur lors de la suppression");
      }
    } catch (err) {
      alert("Erreur serveur");
      console.error(err);
    }
  };

  return (
    <div className={`relative min-h-screen overflow-x-hidden ${isOffline ? "bg-red-800" : "bg-[#0F0F0F]"} text-white`}>
      <AdminNavbar />
      <AnimatedBackground />

      {showPricePopup && (
        <div className="fixed top-0 left-0 w-full h-full z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-[#1e1e1e] border border-green-500 px-10 py-6 rounded-xl shadow-xl animate-pulse">
            <h2 className="text-4xl sm:text-5xl font-extrabold text-green-400 text-center">
              {priceToDisplay}
            </h2>
            <p className="text-center text-white mt-2">Prix final</p>
          </div>
        </div>
      )}

      <div className="relative z-10 px-4 md:px-12 py-8">
        <h1 className="text-3xl md:text-5xl font-bold text-center text-blue-400 drop-shadow mb-4">
          🎮 Gestion en temps réel
        </h1>

        {user?.username && (
          <div className="text-center mb-6">
            <p
              className="text-gray-300 text-md sm:text-lg cursor-pointer hover:text-red-400 underline transition"
              onClick={handleLogout}
            >
              Connecté en tant que : <span className="text-white font-semibold">{user.username}</span>
            </p>
            <div className="text-green-400 mt-2 text-sm sm:text-base">
              Recette du jour : <strong>{dailyRevenue} DT</strong>
            </div>

            <div className="flex justify-center gap-4 flex-wrap mt-4">
              <button
                onClick={handleOpenJournal}
                className="px-5 py-2 bg-blue-600 rounded hover:bg-blue-700 transition text-sm sm:text-base"
              >
                📘 Cahier du jour
              </button>

              {user?.role === "gerant" && (
                <button
                  onClick={() => navigate("/caisse")}
                  className="px-5 py-2 bg-yellow-600 rounded hover:bg-yellow-700 transition text-sm sm:text-base"
                >
                  💼 Caisse
                </button>
              )}
            </div>
          </div>
        )}

        {isOffline && (
          <div className="bg-red-600 text-white text-center py-2 px-4 rounded mb-4 mx-auto max-w-md font-semibold text-lg">
            🚫 Vous êtes hors ligne. Certaines actions sont désactivées.
          </div>
        )}

        <ConsolesBoard
          consoles={consoles}
          setConsoles={setConsoles}
          isOffline={isOffline}
          onSessionEnded={handleShowPrice}
        />

        {showJournal && (
          <div className="mt-10 bg-[#1a1a1a] p-4 sm:p-6 rounded-lg shadow-lg max-w-6xl mx-auto">
            <h2 className="text-xl font-bold text-center text-blue-400 mb-4">
              📘 Sessions du jour
            </h2>

            {todaySessions.length === 0 ? (
              <p className="text-center text-gray-400">Aucune session aujourd’hui.</p>
            ) : (
              <div className="grid grid-cols-1 md:table w-full gap-4">
                <table className="hidden md:table w-full text-sm text-center border-collapse">
                  <thead>
                    <tr className="text-green-400 bg-[#161616]">
                      <th className="py-2 px-2">Console</th>
                      <th className="px-2">Jeu</th>
                      <th className="px-2">Type</th>
                      <th className="px-2">Qté</th>
                      <th className="px-2">Prix</th>
                      <th className="px-2">Heure</th>
                      <th className="px-2">Gérant</th>
                      {user?.role === "admin" && <th className="px-2">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {todaySessions.map((s, i) => (
                      <tr
                        key={i}
                        className={`border-b border-gray-800 ${i % 2 === 0 ? "bg-[#121212]" : "bg-[#181818]"}`}
                      >
                        <td className="py-1 px-2">{s.consoleName}</td>
                        <td className="px-2">{s.game}</td>
                        <td className="px-2">{s.type}</td>
                        <td className="px-2">{s.quantity}</td>
                        <td className="text-green-400 px-2">{s.price} DT</td>
                        <td className="text-gray-400 px-2">{s.formattedDate}</td>
                        <td className="text-white px-2">{s.manager || "—"}</td>
                        {user?.role === "admin" && (
                          <td className="px-2">
                            <button
                              className="text-red-500 hover:text-red-700 underline"
                              onClick={() => handleDeleteSession(s._id)}
                            >
                              Supprimer
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile */}
                <div className="md:hidden flex flex-col gap-4">
                  {todaySessions.map((s, i) => (
                    <div key={i} className="bg-[#161616] border border-gray-800 rounded-lg p-4 shadow-md">
                      <p><strong>🎮 Console :</strong> {s.consoleName}</p>
                      <p><strong>🕹️ Jeu :</strong> {s.game}</p>
                      <p><strong>📂 Type :</strong> {s.type}</p>
                      <p><strong>🔢 Qté :</strong> {s.quantity}</p>
                      <p className="text-green-400"><strong>💰 Prix :</strong> {s.price} DT</p>
                      <p className="text-gray-400"><strong>🕒 Heure :</strong> {s.formattedDate}</p>
                      <p><strong>👤 Gérant :</strong> {s.manager || "—"}</p>
                      {user?.role === "admin" && (
                        <button
                          className="text-red-500 hover:text-red-700 underline mt-2"
                          onClick={() => handleDeleteSession(s._id)}
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
